/* eslint-disable @reatom/unit-naming-rule */
import { action, noop, reatomMap, withOnUpdate } from '@reatom/framework'

import { Iterable, pipe } from '@blade/std-lib'

import { PairAddress } from '../model'

import type { PairModel } from './reatom-pair'
import type { Atom, Unsubscribe } from '@reatom/framework'

type BulkSubscriptionOptions = {
  name?: string
  /**
   * The model atom whose connection opens the subscription. Defaults to the
   * pool's card updates; the trade stream is the other one.
   */
  trigger?: (pair: PairModel) => Atom<unknown>
  /**
   * How many of the given pairs to subscribe, from the front.
   *
   * A column holds twenty pools and shows four. The trade stream is worth the
   * bandwidth for what someone is looking at and not for the whole list: a
   * busy pool alone sends a couple of frames a second.
   */
  limit?: number
}

export const reatomPairInfoBulkSubscription = ({
  name,
  trigger = (pair) => pair.pairInfoSubscriptionTrigger,
  limit,
}: BulkSubscriptionOptions) => {
  const pairInfoSubscriptions = reatomMap<string, Unsubscribe>([], name).pipe(
    withOnUpdate((_ctx, _map) => {
      // console.log(`${name} on update`, map)
    }),
  )

  const processPairsAction = action((ctx, allPairs: Array<PairModel>) => {
    const pairs = limit === undefined ? allPairs : allPairs.slice(0, limit)
    const pairsById = pipe(
      pairs,
      Iterable.toMap((pair) => pair.address),
    )

    for (const pair of pairs) {
      const subscription = pairInfoSubscriptions.get(ctx, pair.address)
      if (!subscription) {
        pairInfoSubscriptions.set(
          ctx,
          pair.address,
          ctx.subscribe(trigger(pair), noop),
        )
      }
    }

    for (const [pairAddress, unsubscribe] of ctx.get(pairInfoSubscriptions)) {
      const pair = pairsById.get(PairAddress(pairAddress))
      if (!pair) {
        unsubscribe()
        pairInfoSubscriptions.delete(ctx, pairAddress)
      }
    }
  })

  const unsubscribeAllAction = action((ctx) => {
    for (const [_, unsubscribe] of ctx.get(pairInfoSubscriptions)) {
      unsubscribe()
    }
    pairInfoSubscriptions.clear(ctx)
  })

  return {
    processPairs: processPairsAction,
    unsubscribeAll: unsubscribeAllAction,
  }
}
